import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/site/BrandMark";
import { LayoutDashboard, Users, CalendarDays, Clock, Inbox, PenSquare, LogOut, ExternalLink, Sparkles, Menu, X, ShieldCheck, DatabaseBackup, Wallet, FileText, Plug } from "lucide-react";

const nav = [
  { to: "/dashboard", end: true, label: "Painel", icon: LayoutDashboard },
  { to: "/dashboard/agenda", label: "Agenda & Sessões", icon: CalendarDays },
  { to: "/dashboard/disponibilidade", label: "Horários disponíveis", icon: Clock },
  { to: "/dashboard/pacientes", label: "Pacientes", icon: Users },
  { to: "/dashboard/financeiro", label: "Financeiro", icon: Wallet },
  { to: "/dashboard/assistente", label: "Assistente IA", icon: Sparkles },
  { to: "/dashboard/mensagens", label: "Mensagens", icon: Inbox },
  { to: "/dashboard/site", label: "Conteúdo do site", icon: PenSquare },
  { to: "/dashboard/seguranca", label: "Segurança", icon: ShieldCheck },
  { to: "/dashboard/backup", label: "Backup", icon: DatabaseBackup },
  { to: "/dashboard/seo", label: "SEO e Conteúdo", icon: FileText },
  { to: "/dashboard/integracoes", label: "Integrações", icon: Plug },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 font-body text-sm transition-colors duration-300 ${
      isActive ? "bg-[#5B6E5B] text-[#FDFBF7]" : "text-[#C7CFC2] hover:bg-white/5"
    }`;

  return (
    <div className="min-h-screen bg-[#F5F0E6] flex">
      {/* desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-[#2C332A] text-[#C7CFC2] p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-10">
          <BrandMark className="w-10 h-10" color="#C8A98E" />
          <div className="leading-none">
            <p className="font-script text-2xl text-[#FDFBF7]">Tacilaine</p>
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-[#8A9A82]">Painel</p>
          </div>
        </div>
        <nav className="flex flex-col gap-1.5 flex-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`dnav-${n.to.split("/").pop() || "home"}`} className={linkClass}>
              <n.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 pt-6 border-t border-white/10 space-y-1.5">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-2.5 font-body text-sm text-[#C7CFC2] hover:bg-white/5 transition-colors">
            <ExternalLink className="w-[18px] h-[18px]" strokeWidth={1.5} /> Ver site
          </a>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 font-body text-sm text-[#C7CFC2] hover:bg-white/5 transition-colors">
            <LogOut className="w-[18px] h-[18px]" strokeWidth={1.5} /> Sair
          </button>
          <p className="px-4 pt-3 font-body text-[11px] text-[#6b7a68] truncate">{user?.email}</p>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* mobile top bar */}
        <div className="lg:hidden sticky top-0 z-40 bg-[#2C332A] text-[#FDFBF7] px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark className="w-8 h-8" color="#C8A98E" />
            <span className="font-script text-2xl leading-none">Painel</span>
          </div>
          <button onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu" className="p-2 -mr-2 rounded-lg active:bg-white/10">
            {menuOpen ? <X className="w-6 h-6" strokeWidth={1.6} /> : <Menu className="w-6 h-6" strokeWidth={1.6} />}
          </button>
        </div>

        {/* mobile dropdown menu */}
        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setMenuOpen(false)}
                className="lg:hidden fixed inset-0 top-[56px] z-30 bg-black/30"
                data-testid="mobile-menu-overlay"
              />
              <motion.nav
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden sticky top-[56px] z-40 bg-[#2C332A] overflow-hidden border-t border-white/10"
                data-testid="mobile-menu"
              >
                <div className="p-3 flex flex-col gap-1">
                  {nav.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)} data-testid={`mnav-${n.to.split("/").pop() || "home"}`} className={linkClass}>
                      <n.icon className="w-5 h-5" strokeWidth={1.5} />
                      {n.label}
                    </NavLink>
                  ))}
                  <div className="mt-2 pt-2 border-t border-white/10 flex flex-col gap-1">
                    <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-3 font-body text-sm text-[#C7CFC2] active:bg-white/10">
                      <ExternalLink className="w-5 h-5" strokeWidth={1.5} /> Ver site
                    </a>
                    <button onClick={doLogout} data-testid="logout-btn-mobile" className="w-full flex items-center gap-3 rounded-xl px-4 py-3 font-body text-sm text-[#C7CFC2] active:bg-white/10">
                      <LogOut className="w-5 h-5" strokeWidth={1.5} /> Sair
                    </button>
                  </div>
                </div>
              </motion.nav>
            </>
          )}
        </AnimatePresence>

        <main className="flex-1 p-5 sm:p-8 lg:p-10 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
